import React from 'react'
import styled, { css } from 'styled-components'
import { Collapse } from 'reactstrap'
import shortid from 'shortid'
import { List } from 'immutable'

import map from 'ramda/src/map'

import Typography from '../../lib/typography'
import { parseFinancial } from '../../lib/utils'

import { createTableCard } from '../../components/project-profit-sheet/TableCard'
import IndicatorCost, * as IndicatorCostLayout from '../../components/project-profit-sheet/IndicatorCost'

import { determineColor, determineStatusColor } from '../../pages/project-profit-sheet/model'

import arrow from '../../static/images/arrow-up-black.svg'

const BaseCard = createTableCard(css`
  grid-template-columns: repeat(6, calc(100% / 6));
`)

const Card = {
  ...BaseCard,
  Container: styled(BaseCard.Container)`
    ${IndicatorCostLayout.Root} {
      width: 95%;
      margin-left: 8px;
      grid-column: span 2;
    }
  `,
}

const Icon = styled.img`
  height: 10px;
  margin-right: 10px;
  transform: ${props => (!props.isOpen ? 'rotate(90deg)' : 'rotate(180deg)')};
`

/**
 * @typedef {{ record: string[], children?: CollapseTree[] }} CollapseTree
 */

/**
 * @typedef {object} Props
 * @property {CollapseTree[]} data
 * @property {string} type
 */

/**
 * @typedef {object} State
 * @property {List<boolean>} collapses
 */

/**
 * @extends {React.Component<Props, State>}
 */
class OverallSummary extends React.Component {
  state = {
    collapses: List(Array(4).fill(false)),
  }

  /**
   * @param {number} idx
   */
  toggleCollapse = idx => () =>
    this.setState({
      collapses: this.state.collapses.update(idx, v => !v),
    })

  statusColorFactory = data => {
    return determineStatusColor(
      map(parseFinancial, {
        forecast: data[1],
        initial: data[2],
        actual: data[3].bars ? data[3].bars[0].value : data[3],
        remaining: data[3].bars ? data[3].bars[1].value : data[3],
      })
    )
  }

  render = () => {
    return (
      <Card.Root topic="Overall Summary">
        <Card.Heading>
          <Typography.Data>Name</Typography.Data>
          <Typography.Data>Forecast</Typography.Data>
          <Typography.Data>Approved Plan</Typography.Data>
          <Typography.Data style={ { paddingLeft: 8 } }>Progress</Typography.Data>
          <div />
          <Typography.Data title="Forecast - Approved Plan">
            Forecast - Approved Plan
          </Typography.Data>
        </Card.Heading>
        {this.props.data.map(({ record, children }, index) => (
          <React.Fragment key={ shortid.generate() }>
            <Card.Container style={ { cursor: 'pointer' } } onClick={ this.toggleCollapse(index) }>
              <Typography.Data>
                <Icon src={ arrow } alt="arrow" isOpen={ this.state.collapses.get(index) } />
                {record[0]}
              </Typography.Data>
              <Typography.Data disc={ this.statusColorFactory(record) }>{record[1]}</Typography.Data>
              <Typography.Data disc="black">{record[2]}</Typography.Data>
              <IndicatorCost
                threshold={ 0.01 }
                hideLabel
                bars={ record[3].bars }
                markers={ record[3].markers }
              />
              <Typography.Data color={ determineColor(record[4]) }>{record[4]}</Typography.Data>
            </Card.Container>
            <Collapse isOpen={ this.state.collapses.get(index) }>
              {children &&
                children.map(({ record: record1, children: children1 }) => (
                  <React.Fragment key={ shortid.generate() }>
                    <Card.Container nested={ 1 }>
                      <Typography.Data weight="normal">{record1[0]}</Typography.Data>
                      <Typography.Data disc={ this.statusColorFactory(record1) } weight="normal">
                        {record1[1]}
                      </Typography.Data>
                      <Typography.Data disc="black" weight="normal">
                        {record1[2]}
                      </Typography.Data>
                      {typeof record1[3] === 'string' && this.props.type === 'SUPPORT' ? (
                        <Typography.Data weight="normal" style={ { gridColumn: 'span 2' } }>
                          Actual {record1[3]}
                        </Typography.Data>
                      ) : (
                        <IndicatorCost
                          threshold={ 0.01 }
                          hideLabel
                          bars={ record1[3].bars }
                          markers={ record1[3].markers }
                        />
                      )}
                      <Typography.Data weight="normal">{record1[4]}</Typography.Data>
                    </Card.Container>
                    {children1 &&
                      children1.map(({ record: record2 }) => (
                        <Card.Container nested={ 2 } key={ shortid.generate() }>
                          <Typography.Data weight="normal">{record2[0]}</Typography.Data>
                          <Typography.Data disc={ this.statusColorFactory(record2) } weight="normal">
                            {record2[1]}
                          </Typography.Data>
                          <Typography.Data disc="black" weight="normal">
                            {record2[2]}
                          </Typography.Data>
                          <IndicatorCost
                            threshold={ 0.01 }
                            hideLabel
                            bars={ record2[3].bars }
                            markers={ record2[3].markers }
                          />
                          <Typography.Data weight="normal">{record2[4]}</Typography.Data>
                        </Card.Container>
                      ))}
                  </React.Fragment>
                ))}
            </Collapse>
          </React.Fragment>
        ))}
      </Card.Root>
    )
  }
}

export default OverallSummary



// WEBPACK FOOTER //
// ./src/components/project-profit-sheet/OverallSummary.jsx